import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, QrCode } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const services = [
    'AC Install & Repair',
    'Carpentry',
    'Electrical',
    'Flooring',
    'Home Renovation',
    'Painting',
    'Plumbing',
    'Roofing',
    'Sheetrock / Drywall',
  ];

  const quickLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Our Process', path: '/process' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <footer data-testid="footer" className="bg-navy text-white grain-overlay">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <div>
              <h3 className="text-2xl font-playfair font-bold">C.A.B Enterprises</h3>
              <p className="text-tan text-sm tracking-widest uppercase mt-1">of Texas</p>
            </div>
            <p className="text-white/70 text-sm leading-relaxed font-inter">
              30 years of hands-on expertise. Full-service contractor delivering the care and service the industry often fails to provide.
            </p>
            {/* Payment Button */}
            <Dialog>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  className="border border-copper text-copper hover:bg-copper hover:text-white text-xs uppercase tracking-widest"
                  data-testid="footer-payment-btn"
                >
                  <QrCode className="mr-2 h-4 w-4" />
                  Make a Payment
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md bg-white">
                <DialogHeader>
                  <DialogTitle className="font-playfair text-navy text-2xl">Pay with Zelle</DialogTitle>
                  <DialogDescription className="text-navy/60">
                    Scan the QR code below with your banking app to send payment
                  </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col items-center py-6">
                  <div className="bg-white p-4 rounded-lg shadow-lg">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_construct-hub-276/artifacts/0bmfs3a5_Zell.jpeg" 
                      alt="Zelle QR Code"
                      className="w-48 h-48 object-cover object-center"
                      style={{ objectPosition: '50% 35%' }}
                    />
                  </div>
                  <p className="mt-4 text-navy font-inter text-sm text-center font-medium">
                    C.A.B ENTERPRISES OF TEXAS
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-playfair font-semibold mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <Link 
                    to="/services" 
                    className="text-white/70 hover:text-copper text-sm font-inter transition-colors"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-playfair font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-white/70 hover:text-copper text-sm font-inter transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-playfair font-semibold mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Phone size={18} className="text-copper mt-0.5 flex-shrink-0" />
                <div>
                  <a href="tel:+19453584803" className="text-white/90 hover:text-copper font-inter text-sm transition-colors">
                    (945) 358-4803
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail size={18} className="text-copper mt-0.5 flex-shrink-0" />
                <div>
                  <a href="mailto:info@cabenterprisesoftexas.com" className="text-white/90 hover:text-copper font-inter text-sm transition-colors">
                    info@cabenterprisesoftexas.com
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin size={18} className="text-copper mt-0.5 flex-shrink-0" />
                <p className="text-white/70 font-inter text-sm">
                  DFW Metroplex
                </p>
              </li>
              <li className="flex items-start space-x-3">
                <Clock size={18} className="text-copper mt-0.5 flex-shrink-0" />
                <div className="text-white/70 font-inter text-sm">
                  <p>Mon-Fri: 8:30am - 6pm</p>
                  <p className="text-copper">Emergency services on weekends</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-white/50 text-sm font-inter">
              &copy; {currentYear} C.A.B Enterprises of Texas, LLC. All rights reserved.
            </p>
            <div className="text-sm font-inter">
              <span className="text-white/50">Licensed & Insured</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
